import { GridColDef, GridValueGetterParams } from "@mui/x-data-grid";
import {
  ClientReadableText,
  ExtraDataEvents,
  HeaderWithTypeField,
  AuditedExtraData,
  LinkComponent,
  SuperviseActions,
  ReasonsArrayDisplay,
  CsrAuditedEvents,
  WebPaymentStatusColumn,
  OldExtraDataEvents
} from "../../core/et3-table/UserCustomHelpers";
import {
  isDefinedString,
  isNumber,
  isPositiveNumber,
  reduce
} from "et3-type-guards";
import Et3Button from "../../core/et3-button";
import getPoolStore from "../../stores/auditor-store/pool-store";

export const EVENTS_COUNT_HEADERS: GridColDef[] = [
  {
    headerName: "id",
    field: "uniqueId"
  },
  {
    headerName: "Type",
    field: "type",
    renderCell: (params: GridValueGetterParams) => {
      return <HeaderWithTypeField value={params.row.type} />;
    }
  },
  {
    headerName: "Total Clean",
    field: "totalClean"
  },
  {
    headerName: "Total Issues",
    field: "totalIssue"
  }
];

export const ADMIN_AUDIT_LIST_HEADERS: GridColDef[] = [
  {
    headerName: "NO#",
    field: "uniqueId"
  },
  {
    headerName: "Date",
    field: "date",
    flex: 1.3
  },
  {
    headerName: "User ID",
    field: "userId",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <LinkComponent
          href={`/users/advanced?id=${params.row.userId}`}
          text={`${params.row.userId}`}
          style={{}}
        />
      );
    }
  },
  {
    headerName: "Gender",
    field: "gender"
  },
  {
    headerName: "Username",
    field: "username"
  },
  {
    headerName: "Type",
    field: "coloredType",
    renderCell: (params: GridValueGetterParams) => {
      return <HeaderWithTypeField value={params.row.type} />;
    }
  },
  {
    headerName: "Old Extra",
    field: "auditOldExtraData",
    flex: 3,
    renderCell: (params: GridValueGetterParams) => {
      return (
        <OldExtraDataEvents
          eventType={params.row.type}
          extraData={params.row.extraData}
        />
      );
    }
  },
  {
    headerName: "Extra",
    field: "auditExtraData",
    flex: 3,
    renderCell: (params: GridValueGetterParams) => {
      return (
        <ExtraDataEvents
          eventType={params.row.type}
          imageSource={params.row.image_source}
          description={params.row.description}
          reportID={params.row.report_id}
          temporaryBanNumberOfDays={params.row.temporaryBanNumberOfDays}
          temporaryBanUntil={params.row.temporaryBanUntil}
          investigationCaseID={params.row.investigationCaseID}
          extraData={params.row.extraData}
        />
      );
    }
  },
  {
    headerName: "Page Name",
    field: "pageName",
    renderCell: (params: GridValueGetterParams) => {
      const { pageName, userId, reportID } = params.row;
      let hrefVal = `/users/advanced?id=${userId}`;
      if (isPositiveNumber(userId)) {
        if (pageName === "USER_ADVANCED") {
          hrefVal = `/users/advanced?id=${userId}`;
        }
        if (pageName === "USER_CONVERSATIONS") {
          hrefVal = `/users/messages?id=${userId}`;
        }
        if (pageName === "REPORTS") {
          hrefVal = `/reports?id=${reportID}`;
        }
      }
      return isDefinedString(pageName) ? (
        <LinkComponent href={`${hrefVal}`} text={`${pageName}`} style={{}} />
      ) : (
        <LinkComponent href={`${hrefVal}`} text={`USER ADVANCED`} style={{}} />
      );
    }
  },
  {
    headerName: "Action",
    field: "audit_actions",
    flex: 2,
    renderCell: (params: GridValueGetterParams) => {
      const auditID = reduce(() => params.row.id, -1);
      const auditUserID = reduce(() => params.row.userId, -1);
      const auditUserUUID = reduce(() => params.row.userUUID, -1);
      const auditType = reduce(() => params.row.type, "");
      return (
        <SuperviseActions
          type={"AUDIT_REASON"}
          fromPage={"AUDITOR_AUDIT"}
          event_id={auditID}
          user_id_number={auditUserID}
          userUUID={auditUserUUID}
          event_type={auditType}
        />
      );
    }
  }
];

export const AUDITED_EVENTS_COUNTS_HEADERS: GridColDef[] = [
  {
    headerName: "Event Id",
    field: "event_id"
  },
  {
    headerName: "User ID",
    field: "user_id",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <LinkComponent
          href={`/users/advanced?id=${params.row.user_id}`}
          text={`${params.row.user_id}`}
          style={{}}
        />
      );
    }
  },
  {
    headerName: "Type",
    field: "type",
    renderCell: (params: GridValueGetterParams) => {
      return <HeaderWithTypeField value={params.row.type} />;
    }
  },

  {
    headerName: "Extra Data",
    field: "audited_extra_data",
    flex: 3,
    renderCell: (params: GridValueGetterParams) => {
      return (
        <AuditedExtraData
          auditedDataType={params.row.type}
          auditedPageName={params.row.page_name}
          auditedImageSource={params.row.image_source}
          auditedDescription={params.row.description}
          auditedReportID={params.row.report_id}
          auditedInvestigationcCaseID={params.row.investigationCaseID}
          auditedTemporaryBanNumberOfDays={params.row.temporaryBanNumberOfDays}
          auditedTemporaryBanUntil={params.row.temporaryBanUntil}
          auditedExtraData={params.row.extra_data}
        />
      );
    }
  },
  {
    headerName: "Page Name",
    field: "page_name",
    renderCell: (params: GridValueGetterParams) => {
      return <ClientReadableText text={params.row.page_name} />;
    }
  },
  {
    headerName: "Event Created On",
    field: "created_on"
  },
  {
    headerName: "Event Done By",
    field: "event_done_by"
  },
  {
    headerName: "Issue Type",
    field: "audit_issue_type"
  },
  {
    headerName: "Event Audited On",
    field: "audited_on"
  },
  {
    headerName: "Event Audited By",
    field: "audited_by"
  },
  {
    headerName: "Reason",
    field: "audited_events_reasons",
    renderCell: (params: GridValueGetterParams) => {
      return <ReasonsArrayDisplay reasons={params.row.reasons} />;
    }
  },
  {
    headerName: "Proof",
    field: "proof"
  }
];

export const AUDITOR_MY_AUDITING_PERFORMANCE_EFFICIENCY: GridColDef[] = [
  {
    headerName: "Id",
    field: "uniqueId"
  },
  {
    headerName: "Event Name",
    field: "event_name",
    renderCell: (params: GridValueGetterParams) => {
      return <ClientReadableText text={params.row.event_name} />;
    }
  },
  {
    headerName: "Supervised events",
    field: "supervised_events"
  },
  {
    headerName: "Minor Error",
    field: "my_minor_errors"
  },
  {
    headerName: "Major Errors",
    field: "my_major_errors"
  },
  {
    headerName: "Total Errors",
    field: "total_errors"
  }
];

export const AUDITOR_MY_AUDITING_PERFORMANCE_HEADERS: GridColDef[] = [
  {
    headerName: "Id",
    field: "uniqueId"
  },
  {
    headerName: "Audit",
    field: "auditName"
  },
  {
    headerName: "Major Issues",
    field: "major"
  },
  {
    headerName: "Minor Issues",
    field: "minor"
  },
  {
    headerName: "No Issues",
    field: "no_issue"
  },
  {
    headerName: "Total",
    field: "total"
  }
];

export const AUDITOR_MY_AUDITING_MISTAKES_HEADERS: GridColDef[] = [
  {
    headerName: "E ID",
    field: "id"
  },
  {
    headerName: "User ID",
    field: "user_id",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <LinkComponent
          href={`/users/advanced?id=${params.row.user_id}`}
          text={`${params.row.user_id}`}
          style={{}}
        />
      );
    }
  },
  {
    headerName: "Type",
    field: "type",
    flex: 1.5,
    renderCell: (params: GridValueGetterParams) => {
      return <HeaderWithTypeField value={params.row.type} />;
    }
  },
  {
    headerName: "Old Data",
    field: "oldExtraDataAuditorMistakes",
    flex: 4,
    renderCell: (params: GridValueGetterParams) => {
      return (
        <OldExtraDataEvents
          eventType={params.row.type}
          extraData={params.row.extra_data}
        />
      );
    }
  },
  {
    headerName: "Data",
    field: "extraDataAuditorMistakes",
    flex: 4,
    renderCell: (params: GridValueGetterParams) => {
      return (
        <ExtraDataEvents
          eventType={params.row.type}
          imageSource={params.row.image_source}
          description={params.row.description}
          reportID={params.row.report_id}
          temporaryBanNumberOfDays={params.row.temporaryBanNumberOfDays}
          temporaryBanUntil={params.row.temporaryBanUntil}
          investigationCaseID={params.row.investigationCaseID}
          extraData={params.row.extra_data}
        />
      );
    }
  },
  {
    headerName: "Page",
    field: "page_name",
    renderCell: (params: GridValueGetterParams) => {
      return <ClientReadableText text={params.row.page_name} />;
    }
  },
  {
    headerName: "Created",
    field: "created_on"
  },
  {
    headerName: "C By",
    field: "event_done_by"
  },
  {
    headerName: "AUD Issue",
    field: "issueTypeByCSR"
  },
  {
    headerName: "Audited",
    field: "audited_on"
  },
  {
    headerName: "A By",
    field: "audited_by"
  },
  {
    headerName: "SUP Issue",
    field: "issueTypeBySupervisor"
  },
  {
    headerName: "Supervised",
    field: "supervised_on"
  },
  {
    headerName: "S By",
    field: "supervised_by"
  },
  {
    headerName: "Proof",
    field: "proof"
  },
  {
    headerName: "Reason",
    field: "tbw_reasons",
    flex: 1.5,
    renderCell: (params: GridValueGetterParams) => {
      return <ReasonsArrayDisplay reasons={params.row.reasons} />;
    }
  }
];

export const CSR_PERFORMANCE_PERFORMANCE_HEADERS: GridColDef[] = [
  {
    headerName: "id",
    field: "uniqueId"
  },
  {
    headerName: "Page Name",
    field: "page_name",
    renderCell: (params: GridValueGetterParams) => {
      return <ClientReadableText text={params.row.pageName} />;
    }
  },
  {
    headerName: "Time",
    field: "activeTime"
  },
  {
    headerName: "Events",
    field: "numberOfEvents"
  },
  {
    headerName: "Clean Pool Event count",
    field: "cleanPoolCount"
  },
  {
    headerName: "Events/hour",
    field: "eventPerHour"
  }
];

export const CSR_PERFORMANCE_EFFECTIVENESS_HEADERS: GridColDef[] = [
  {
    headerName: "Event Name",
    field: "event_name",
    renderCell: (params: GridValueGetterParams) => {
      return <ClientReadableText text={params.row.event_name} />;
    }
  },
  {
    headerName: "Audited events",
    field: "audited_events",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <CsrAuditedEvents
          textValue={params.row.audited_events}
          issueType={"all"}
        />
      );
    }
  },
  {
    headerName: "Minor errors",
    field: "minor_errors",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <CsrAuditedEvents
          textValue={params.row.minor_errors}
          issueType={"minor"}
        />
      );
    }
  },
  {
    headerName: "% Minor errors",
    field: "minor_errors_percentage"
  },
  {
    headerName: "Major errors",
    field: "major_errors",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <CsrAuditedEvents
          textValue={params.row.major_errors}
          issueType={"major"}
        />
      );
    }
  },
  {
    headerName: "% Major errors",
    field: "major_errors_percentage"
  },
  {
    headerName: "Total errors",
    field: "total_issues"
  },
  {
    headerName: "% Total errors",
    field: "total_errors_percentage"
  }
];

export const EVENTS_CHARTS_DAILY_STATS: GridColDef[] = [
  {
    headerName: "Date",
    field: "stat_date",
    flex: 4
  },
  {
    headerName: "Id",
    field: "uniqueId"
  },
  {
    headerName: "0",
    field: "stat_0"
  },
  {
    headerName: "1",
    field: "stat_1"
  },
  {
    headerName: "2",
    field: "stat_2"
  },
  {
    headerName: "3",
    field: "stat_3"
  },
  {
    headerName: "4",
    field: "stat_4"
  },
  {
    headerName: "5",
    field: "stat_5"
  },
  {
    headerName: "6",
    field: "stat_6"
  },
  {
    headerName: "7",
    field: "stat_7"
  },
  {
    headerName: "8",
    field: "stat_8"
  },
  {
    headerName: "9",
    field: "stat_9"
  },
  {
    headerName: "10",
    field: "stat_10"
  },
  {
    headerName: "11",
    field: "stat_11"
  },
  {
    headerName: "12",
    field: "stat_12"
  },
  {
    headerName: "13",
    field: "stat_13"
  },
  {
    headerName: "14",
    field: "stat_14"
  },
  {
    headerName: "15",
    field: "stat_15"
  },
  {
    headerName: "16",
    field: "stat_16"
  },
  {
    headerName: "17",
    field: "stat_17"
  },
  {
    headerName: "18",
    field: "stat_18"
  },
  {
    headerName: "19",
    field: "stat_19"
  },
  {
    headerName: "20",
    field: "stat_20"
  },
  {
    headerName: "21",
    field: "stat_21"
  },
  {
    headerName: "22",
    field: "stat_22"
  },
  {
    headerName: "23",
    field: "stat_23"
  }
];

export const ADMIN_POOLS_HEADERS: GridColDef[] = [
  {
    headerName: "id",
    field: "uniqueId"
  },
  {
    headerName: "Admin Id",
    field: "adminId"
  },
  {
    headerName: "Full Name",
    field: "fullName"
  },
  {
    headerName: "Action",
    field: "totalCount"
  },
  {
    headerName: "Clean Pool",
    field: "cleanPool",
    flex: 1.2,
    renderCell: (params: GridValueGetterParams) => {
      const { totalCount, cleanPoolAdminId, cleanPoolType } =
        params.row.cleanPool;
      return isPositiveNumber(totalCount) ? (
        <Et3Button
          isEnabled={true}
          onClick={async () => {
            const definedAdminId = isNumber(cleanPoolAdminId)
              ? cleanPoolAdminId
              : parseInt(cleanPoolAdminId, 10);
            await getPoolStore().cleanPool(definedAdminId, cleanPoolType);
          }}
          size="MEDIUM"
          text="CLEAN_POOL"
          type="REJECT"
        />
      ) : null;
    }
  }
];

export const AUDITOR_WORKLOAD_DATA_HEADERS: GridColDef[] = [
  {
    headerName: "id",
    field: "uniqueId"
  },
  {
    headerName: "Event Name",
    field: "event_name",
    renderCell: (params: GridValueGetterParams) => {
      return <ClientReadableText text={params.row.event_name} />;
    }
  },
  {
    headerName: "Incoming",
    field: "incoming"
  },
  {
    headerName: "Outgoing by CSR",
    field: "outgoingByCSRTotal"
  },
  {
    headerName: "Outgoing by AI",
    field: "outgoingByAI"
  },
  {
    headerName: "Difference",
    field: "difference"
  }
];

export const AUDITOR_WORKLOAD_DIVISION_BY_CSR_HEADERS: GridColDef[] = [
  {
    headerName: "id",
    field: "uniqueId"
  },
  {
    headerName: "Admin",
    field: "adminName"
  },
  {
    headerName: "Descriptions",
    field: "descriptionCount"
  },
  {
    headerName: "Total %",
    field: "descriptionPercentage"
  },
  {
    headerName: "Peer Descriptions",
    field: "peerDescriptionCount"
  },
  {
    headerName: "Total %",
    field: "peerDescriptionPercentage"
  },
  {
    headerName: "Pictures",
    field: "pictureCount"
  },
  {
    headerName: "Total %",
    field: "picturePercentage"
  },
  {
    headerName: "Reports",
    field: "reportCount"
  },
  {
    headerName: "Total %",
    field: "reportPercentage"
  },
  {
    headerName: "Inv Case",
    field: "investigationCount"
  },
  {
    headerName: "Total %",
    field: "investigationPercentage"
  }
];

export const AUDITOR_PROMO_CODE_HEADERS: GridColDef[] = [
  {
    headerName: "Admin",
    field: "adminName"
  },
  {
    headerName: "PC ID",
    field: "promoCodeId"
  },
  {
    headerName: "Code",
    field: "promocode"
  },
  {
    headerName: "DESC",
    field: "description"
  },
  {
    headerName: "Reward(HR)",
    field: "benefitInHours"
  },
  {
    headerName: "Reward Type",
    field: "benefitType"
  },
  {
    headerName: "Start Time",
    field: "startTime"
  },
  {
    headerName: "Expiry Time",
    field: "expiryTime"
  },
  {
    headerName: "Max Usage",
    field: "maxUsage"
  },
  {
    headerName: "Gender",
    field: "promoCodeForGender"
  },
  {
    headerName: "Paid",
    field: "promoCodeForPaid"
  },
  {
    headerName: "Nationality",
    field: "promoCodeForNationality"
  },
  {
    headerName: "Active",
    field: "isActive"
  },
  {
    headerName: "Updated",
    field: "updatedOn"
  },
  {
    headerName: "Created",
    field: "createdOn"
  }
];



export const AUTITOR_PROMO_CODE_SEARCH_HEADERS: GridColDef[] = [
  {
    headerName: "User Id",
    field: "user_id",
    renderCell: (params: GridValueGetterParams) => {
      return (
        <LinkComponent
          href={`/users/advanced?id=${params.row.user_id}`}
          text={`${params.row.user_id}`}
          style={{}}
        />
      );
    }
  },
  {
    headerName: "Get Reward",
    field: "isUserRewarded"
  },
  {
    headerName: "Reward Type",
    field: "rewardType"
  },
  {
    headerName: "Reward Amount In Hours",
    field: "rewardAmountInHours"
  },
  {
    headerName: "Reason",
    field: "reason",
    flex: 1.5,
    renderCell: (params: GridValueGetterParams) => {
      return <ClientReadableText text={params.row.reason} />;
    }
  },
  {
    headerName: "Created On",
    field: "created_on"
  }
];

export const WEB_PAYMENT_HEADERS: GridColDef[] = [
  {
    headerName: "User ID",
    field: "userID",
    sortable: false,
    filterable: false,
    renderCell: (params: GridValueGetterParams) => {
      return (
        <LinkComponent
          href={`/users/advanced?id=${params.row.userID}`}
          text={`${params.row.userID}`}
          style={{}}
        />
      );
    }
  },
  {
    headerName: "Email",
    field: "email",
    sortable: false,
    filterable: false
  },
  {
    headerName: "Name",
    field: "name",
    sortable: false,
    filterable: false
  },
  {
    headerName: "Transaction ID",
    field: "referenceID",
    sortable: false,
    filterable: false
  },
  {
    headerName: "ProductID",
    field: "productID",
    sortable: false,
    filterable: false
  },
  {
    headerName: "TypeOfPayment",
    field: "typeOfPayment",
    sortable: false,
    filterable: false
  },
  {
    headerName: "UserCountryCode",
    field: "userCountryCode",
    sortable: false,
    filterable: false
  },
  {
    headerName: "ExpiryDate",
    field: "expiryDate",
    sortable: false,
    filterable: false
  },
  {
    headerName: "BundlePrice",
    field: "bundlePrice",
    sortable: false,
    filterable: false
  },
  {
    headerName: "BundleCurrency",
    field: "bundleCurrency",
    sortable: false,
    filterable: false
  },
  {
    headerName: "CreatedOn",
    field: "createdOn",
    sortable: false,
    filterable: false
  },
  {
    headerName: "Refund Or Cancel",
    field: "refundOrCancel",
    sortable: false,
    filterable: false,
    flex: 3,
    renderCell: (params: GridValueGetterParams) => {
      const { referenceID, userIDToUseInVIP, paymentColumnStatus } =
        params.row.refundOrCancel;
      const isActiveSubscription = reduce(
        () => params.row.refundOrCancel.isActiveSubscription,
        false
      );
      return (
        <WebPaymentStatusColumn
          referenceID={referenceID}
          userIDToUseInVIP={userIDToUseInVIP}
          paymentColumnStatus={paymentColumnStatus}
          isActiveSubscription={isActiveSubscription}
        />
      );
    }
  }
];

export const AUDITOR_USER_RATE_US_DATA_HEADERS: GridColDef[] = [
  {
    headerName: "ID",
    field: "uniqueId"
  },
  {
    headerName: "Count Positive",
    field: "countPositive",
    sortable: false,
    filterable: false
  },
  {
    headerName: "Count Negative",
    field: "countNegative",
    sortable: false,
    filterable: false
  },
  {
    headerName: "Total",
    field: "total",
    sortable: false,
    filterable: false
  },
  {
    headerName: "Date",
    field: "date",
    sortable: false,
    filterable: false
  },
  {
    headerName: "% Positive",
    field: "percentagePositive",
    sortable: false,
    filterable: false
  },
  {
    headerName: "% Negative",
    field: "percentageNegative",
    sortable: false,
    filterable: false
  }
];

export const AUDITOR_EVENTS_DATA_HEADERS: GridColDef[] = [
  {
    headerName: "Name of CSR/SCSR",
    field: "adminName",
    flex: 2
  },
  {
    headerName: "Report",
    field: "report"
  },
  {
    headerName: "Report %",
    field: "reportPercentage"
  },
  {
    headerName: "Pictures",
    field: "pictures"
  },
  {
    headerName: "Pictures %",
    field: "picturesPercentage"
  },
  {
    headerName: "Description",
    field: "description"
  },
  {
    headerName: "Description %",
    field: "descriptionPercentage"
  },
  {
    headerName: "Peer Description",
    field: "peerDescription"
  },
  {
    headerName: "Peer Description %",
    field: "peerDescriptionPercentage"
  },
  {
    headerName: "IC",
    field: "investigationCases",
    sortable: false,
    filterable: false
  },
  {
    headerName: "IC %",
    field: "investigationCasesPercentage"
  }
];

export const AUDITOR_REPORT_ACTIONS_HEADERS: GridColDef[] = [
  {
    headerName: "Gender",
    field: "gender"
  },
  {
    headerName: "Resolved",
    field: "resolved"
  },
  {
    headerName: "Banned",
    field: "banned"
  },
  {
    headerName: "Temp Ban 1d",
    field: "tempBanOneDay"
  },
  {
    headerName: "Temp Ban 3d",
    field: "tempBanThreeDays"
  },
  {
    headerName: "Temp Ban 5d",
    field: "tempBanFiveDays"
  },
  {
    headerName: "Temp Ban 7d",
    field: "tempBanSevenDays"
  },
  {
    headerName: "Warned",
    field: "warned"
  },
  {
    headerName: "Total",
    field: "total"
  }
];

export const AUDITOR_TBW_EVENT_STATS_HEADERS: GridColDef[] = [
  {
    headerName: "TBW Reason",
    field: "tbwName"
  },
  {
    headerName: "Banned",
    field: "banned"
  },
  {
    headerName: "Temp Ban 1d",
    field: "tempBanOneDay"
  },
  {
    headerName: "Temp Ban 3d",
    field: "tempBanThreeDays"
  },
  {
    headerName: "Temp Ban 5d",
    field: "tempBanFiveDays"
  },
  {
    headerName: "Temp Ban 7d",
    field: "tempBanSevenDays"
  },
  {
    headerName: "Warned",
    field: "warned"
  },
  {
    headerName: "Total",
    field: "total"
  }
];
